import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient, useAuthStore } from '@dukene/shared';
import {
  Truck,
  MapPin,
  Clock,
  CheckCircle,
  Barcode,
  ArrowRight,
  MagnifyingGlass,
  ArrowsClockwise,
  CaretDown,
  CaretRight,
  CircleNotch,
  Compass,
} from '@phosphor-icons/react';

interface ScanEvent {
  id: string;
  eventType: string;
  occurredAt: string;
  notes?: string;
  location?: { id: string; name: string; address?: string };
  scannedBy?: { id: string; name: string; role: string };
}

interface ShipmentRow {
  id: string;
  trackingCode: string;
  status: string;
  tier: string;
  shippingCost: number;
  weightKg: number;
  estimatedDelivery?: string;
  collectedAt?: string;
  createdAt: string;
  originLocation: { id: string; name: string; address?: string };
  destinationLocation: { id: string; name: string; address?: string };
  currentLocation?: { id: string; name: string };
  events: ScanEvent[];
  order: {
    id: string;
    orderNumber: string;
    status: string;
    email: string;
    total: number;
    customer?: { firstName: string; lastName: string; email: string };
  };
}

function formatTime(iso: string) {
  try {
    return new Date(iso).toLocaleString('en-KE', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function ScanTimeline({ events }: { events: ScanEvent[] }) {
  if (!events || events.length === 0) {
    return (
      <div className="flex items-center gap-2 py-3 px-4 bg-[#F6F6F7] dark:bg-slate-900 rounded-lg border border-dashed border-[#D2D5D9] dark:border-slate-800 text-xs text-[#5C5F62]">
        <Clock weight="bold" className="w-4 h-4 text-[#8C9196]" />
        <span>No transit scan events yet — awaiting dispatch point check-in.</span>
      </div>
    );
  }

  return (
    <div className="space-y-3 pl-2 border-l-2 border-[#D2D5D9] dark:border-slate-700 py-1">
      {events.map((ev, idx) => {
        return (
          <div key={ev.id || idx} className="relative pl-4">
            <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#006838] border-2 border-white dark:border-slate-800 flex items-center justify-center">
              <span className="w-1.5 h-1.5 rounded-full bg-white" />
            </span>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
              <div>
                <span className="font-bold text-xs text-[#202223] dark:text-white uppercase tracking-wider">
                  {ev.eventType.replace(/_/g, ' ')}
                </span>
                {ev.location && (
                  <span className="text-xs text-[#5C5F62] ml-2">
                    @ {ev.location.name}
                  </span>
                )}
              </div>
              <span className="text-[11px] font-mono text-[#5C5F62]">
                {formatTime(ev.occurredAt)}
              </span>
            </div>
            {ev.notes && (
              <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
                {ev.notes}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function OrderRoutes() {
  const activeStoreId = useAuthStore((s) => s.activeStoreId);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const {
    data: shipmentsData,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['merchant-order-routes', activeStoreId, statusFilter],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (statusFilter !== 'ALL') params.append('status', statusFilter);
        const res = await apiClient.get<ShipmentRow[] | { shipments: ShipmentRow[] }>(
          `/shipping/shipments?${params.toString()}`
        );
        if (Array.isArray(res)) return res;
        return (res as any)?.shipments || [];
      } catch (err) {
        console.error('Failed to load shipment routes:', err);
        return [] as ShipmentRow[];
      }
    },
    enabled: !!activeStoreId,
  });

  const shipments = Array.isArray(shipmentsData) ? shipmentsData : [];

  const filtered = shipments.filter((s) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      s.trackingCode?.toLowerCase().includes(q) ||
      s.order?.orderNumber?.toLowerCase().includes(q) ||
      s.originLocation?.name?.toLowerCase().includes(q) ||
      s.destinationLocation?.name?.toLowerCase().includes(q)
    );
  });

  const inTransitCount = shipments.filter((s) => s.status === 'IN_TRANSIT').length;
  const atDestCount = shipments.filter((s) => s.status === 'AT_DESTINATION').length;
  const collectedCount = shipments.filter((s) => s.status === 'COLLECTED').length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
            Pending Dispatch
          </span>
        );
      case 'CHECKED_IN':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#EBF5FA] text-[#006FBB]">
            Checked In
          </span>
        );
      case 'IN_TRANSIT':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-400 border border-violet-200 dark:border-violet-800">
            In Transit Route
          </span>
        );
      case 'AT_DESTINATION':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#E3F1DF] text-[#006838]">
            Ready for Pickup
          </span>
        );
      case 'COLLECTED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#E3F1DF] text-[#006838]">
            <CheckCircle weight="fill" className="w-3 h-3" />
            Delivered
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#F6F6F7] text-[#5C5F62]">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="space-y-5">
      {/* Polaris High-Density Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E3F1DF] dark:bg-[#006838]/20 flex items-center justify-center text-[#006838] shrink-0">
            <Compass weight="fill" className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-base font-bold text-[#202223] dark:text-white">
              Logistics & Delivery Routes
            </h1>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
              Monitor active courier transit routes, pickup point arrivals, and live shipment scans
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => refetch()}
          disabled={isRefetching}
          className="h-8 px-3.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white inline-flex items-center gap-1.5 transition-colors shrink-0"
        >
          <ArrowsClockwise
            weight="bold"
            className={`w-3.5 h-3.5 ${isRefetching ? 'animate-spin text-[#006838]' : ''}`}
          />
          Sync Transit Routes
        </button>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button
          type="button"
          onClick={() => setStatusFilter('ALL')}
          className={`bg-white dark:bg-[#1A1D21] border rounded-xl p-3 text-left transition-all ${
            statusFilter === 'ALL'
              ? 'border-[#006838] ring-1 ring-[#006838] bg-[#E3F1DF]/20'
              : 'border-[#D2D5D9] dark:border-slate-800 hover:border-[#8C9196]'
          }`}
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5C5F62]">
            Total Active Routes
          </span>
          <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-0.5">
            {shipments.length}
          </p>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('IN_TRANSIT')}
          className={`bg-white dark:bg-[#1A1D21] border rounded-xl p-3 text-left transition-all ${
            statusFilter === 'IN_TRANSIT'
              ? 'border-violet-500 ring-1 ring-violet-500 bg-violet-50/40'
              : 'border-[#D2D5D9] dark:border-slate-800 hover:border-[#8C9196]'
          }`}
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-violet-700">
            In Transit / On Road
          </span>
          <p className="text-xl font-bold text-violet-600 font-mono mt-0.5">
            {inTransitCount}
          </p>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('AT_DESTINATION')}
          className={`bg-white dark:bg-[#1A1D21] border rounded-xl p-3 text-left transition-all ${
            statusFilter === 'AT_DESTINATION'
              ? 'border-[#006838] ring-1 ring-[#006838] bg-[#E3F1DF]/20'
              : 'border-[#D2D5D9] dark:border-slate-800 hover:border-[#8C9196]'
          }`}
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#006838]">
            At Pickup Hub
          </span>
          <p className="text-xl font-bold text-[#006838] font-mono mt-0.5">
            {atDestCount}
          </p>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('COLLECTED')}
          className={`bg-white dark:bg-[#1A1D21] border rounded-xl p-3 text-left transition-all ${
            statusFilter === 'COLLECTED'
              ? 'border-[#006838] ring-1 ring-[#006838] bg-[#E3F1DF]/20'
              : 'border-[#D2D5D9] dark:border-slate-800 hover:border-[#8C9196]'
          }`}
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5C5F62]">
            Collected / Completed
          </span>
          <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-0.5">
            {collectedCount}
          </p>
        </button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
          />
          <input
            type="text"
            placeholder="Search by tracking code, order #, origin or destination..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white placeholder-[#8C9196] focus:outline-none focus:ring-1 focus:ring-[#006838]"
          />
        </div>

        <div className="text-xs text-[#5C5F62]">
          Showing <b>{filtered.length}</b> active shipments
        </div>
      </div>

      {/* Shipments & Routes Table */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-xs text-[#5C5F62]">
            <CircleNotch weight="bold" className="w-6 h-6 animate-spin mx-auto mb-2 text-[#006838]" />
            Loading transit routes and scan history...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-14 text-center">
            <Truck weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-2" />
            <h3 className="text-sm font-bold text-[#202223] dark:text-white">
              No Transit Routes Found
            </h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1">
              Shipments will appear here once parcels are assigned a tracking barcode.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/60 dark:bg-slate-900/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62]">
                  <th className="py-2.5 px-4 w-8" />
                  <th className="py-2.5 px-4">Tracking & Order #</th>
                  <th className="py-2.5 px-4">Transit Route (Origin → Dest)</th>
                  <th className="py-2.5 px-4">Current Location</th>
                  <th className="py-2.5 px-4">Tier & Weight</th>
                  <th className="py-2.5 px-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800 text-xs">
                {filtered.map((row) => {
                  const isExpanded = expandedId === row.id;

                  return (
                    <div key={row.id} className="contents">
                      <tr
                        onClick={() => setExpandedId(isExpanded ? null : row.id)}
                        className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors cursor-pointer"
                      >
                        <td className="py-3 px-4 text-[#5C5F62]">
                          {isExpanded ? (
                            <CaretDown weight="bold" className="w-3.5 h-3.5 text-[#006838]" />
                          ) : (
                            <CaretRight weight="bold" className="w-3.5 h-3.5" />
                          )}
                        </td>

                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-[#202223] dark:text-white">
                              {row.trackingCode}
                            </span>
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#F6F6F7] dark:bg-slate-800 text-[#5C5F62]">
                              #{row.order?.orderNumber || 'ORD'}
                            </span>
                          </div>
                        </td>

                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1.5 font-semibold text-[#202223] dark:text-white">
                            <span>{row.originLocation?.name || 'Hub'}</span>
                            <ArrowRight weight="bold" className="w-3.5 h-3.5 text-[#8C9196]" />
                            <span>{row.destinationLocation?.name || 'Destination'}</span>
                          </div>
                        </td>

                        <td className="py-3 px-4">
                          <div className="inline-flex items-center gap-1 text-[#5C5F62]">
                            <MapPin weight="fill" className="w-3.5 h-3.5 text-[#006838]" />
                            <span>{row.currentLocation?.name || row.originLocation?.name}</span>
                          </div>
                        </td>

                        <td className="py-3 px-4">
                          <div className="font-semibold text-[#202223] dark:text-white">
                            {row.tier || 'Standard'} • {row.weightKg || '1'} kg
                          </div>
                        </td>

                        <td className="py-3 px-4">{getStatusBadge(row.status)}</td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-[#F6F6F7]/50 dark:bg-slate-900/50">
                          <td colSpan={6} className="p-4">
                            <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-xs">
                              <h4 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62] mb-3 flex items-center gap-1.5">
                                <Barcode weight="bold" className="w-4 h-4 text-[#006838]" />
                                Live Scan History & Transit Milestones — {row.trackingCode}
                              </h4>
                              <ScanTimeline events={row.events || []} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </div>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
